import { create, el } from "@dhx/ts-common/dom";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { View } from "@dhx/ts-common/view";
import { getFormItemCss, getValidationMessage, isEmptyObj } from "../helper";
import { Popup } from "@dhx/ts-popup";
import { ValidationStatus, ICheckbox, BaseElementEvent, IBaseCheckboxEventHandlersMap } from "../types";


export class Checkbox extends View {
	public config: ICheckbox;
	public events: IEventSystem<BaseElementEvent, IBaseCheckboxEventHandlersMap>;
	protected _handlers: any;
	protected _helper: Popup;
	constructor(container: HTMLElement | string, config = {}) {
		super(container, config);
		this._handlers = {
			showHelper: e => {
				e.preventDefault();
				e.stopPropagation();
				this._helper.show(e.target);
			},
			cancelUnusefulClick: e => {
				e.preventDefault();
			},
			onchange: (e: Event) => {
				this.config.checked = (e.target as HTMLInputElement).checked;
				this.events.fire(BaseElementEvent.change, [(e.target as HTMLInputElement).checked]);
				this.validate();
			}
		};
		this.events = new EventSystem();
		this.events.on(BaseElementEvent.change, value => {
			this.config.checked = value;
		});

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	clear(): void {
		this.config.checked = false;
		this.paint();
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	setValue(value: boolean): void {
		this.events.fire(BaseElementEvent.change, [value]);
		this.config.checked = value;
		this.paint();
	}
	getValue(): boolean {
		return this.config.checked || false;
	}
	setConfig(config: ICheckbox): void {
		if (isEmptyObj(config)) {
			return;
		}
		this.config = {
			type: this.config.type,
			id: this.config.id,
			name: this.config.name
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		this.events.fire(BaseElementEvent.configUpdate, [this.config]);
		this.paint();
	}
	validate(): boolean {
		const isValid = !this.config.required || this.config.checked;
		this.config.$validationStatus = isValid
			? ValidationStatus.success
			: ValidationStatus.error;
		this.paint();
		return isValid;
	}
	private _draw() {
		if (this.config.help) {
			if (this._helper) {
				this._helper.attachHTML(this.config.help);
			} else {
				this._helper = new Popup({ css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light" });
				this._helper.attachHTML(this.config.help);
			}
		}
		const { id, value, label, checked, disabled, name, help, labelWidth, labelInline, required, hidden } = this.config;
		const visibility = hidden ? " dhx_form-group--hidden" : "";
		return el("label.dhx_checkbox.dhx_form-group", {
			class: getFormItemCss(this.config, Boolean(required)) + visibility,
			style: { "margin-left": `${labelWidth && labelInline ? `calc(${labelWidth} + 16px)` : ""}` }
		}, [
			el("input.dhx_checkbox__input", {
				type: "checkbox",
				id,
				value: value || "",
				name: name || "",
				disabled,
				checked,
				onchange: this._handlers.onchange,
				required
			}),
			el("span.dhx_checkbox__visual-input"),
			el("span.dhx_label", {
				class: help ? "dhx_label--with-help" : ""
			}, help ? [
				el("span.dhx_label__holder", label),
				el("span.dhx_label-help.dxi.dxi-help-circle-outline", {
					tabindex: "0",
					role: "button",
					onclick: this._handlers.showHelper
				}),
			] : label),
			getValidationMessage(this.config) && el("span.dhx_input-caption", {
				onclick: this._handlers.cancelUnusefulClick,
			}, getValidationMessage(this.config))
		]);
	}
}